import pinia, { useTabBarStore } from '@/store';
import type { Router, RouteRecordNormalized } from 'vue-router';
import { debounce, isEqual, cloneDeep } from 'lodash';
import { JSONParse, JSONStringify } from '@/utils/common';
import { watch, toRaw, getCurrentInstance } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import useElectronOnActivated from '@/hooks/useElectronOnActivated';

// 需要同步的Store
const whiteStoreIds = [
  'app',
  'business',
  'material',
  'message',
  'modalStore',
  'supplier',
  'user',
  'tabBar',
];

const UUID = uuidv4();

export const setupElectronGuard = (router: Router) => {
  let flag = false;
  if (window.electronAPI?.electronPage) {
    window.electronAPI?.onUpdateRoute((to: string) => {
      router.push(JSON.parse(to));
    });

    window.addEventListener('resize', () => {
      window.electronAPI?.emitResize?.();
    });
  }
  // const tab = useTabBarStore();
  router.beforeEach((to, from, next) => {
    if (window.electronAPI?.electronPage) {
      setTimeout(() => {
        window.electronAPI?.switchTab(to.path, to.fullPath);
      }, 0);
    }
    if (window.electronAPI?.singlePage) {
      if (!flag) {
        flag = true;
        next();
        return;
      }
      window.electronAPI?.routeTo(JSON.stringify(to));
      next(false);
      return;
    }
    next();
  });

  if (window.electronAPI?.electronPage || window.electronAPI?.singlePage) {
    window.document.addEventListener('click', (e) => {
      window.electronAPI?.emitDocClick();
    });
  }
};

export const useTabBarSync = () => {
  if (!window.electronAPI?.electronPage) {
    return;
  }
  const tab = useTabBarStore();
  watch(
    () => tab.getTabList,
    (tabList) => {
      console.log(tabList);
      if (tabList) {
        window.electronAPI?.syncTab(JSON.stringify(tabList));
      }
    },
    { deep: true }
  );
};

export const useTabBarSyncForSinglePage = () => {
  if (!window.electronAPI?.singlePage) {
    return;
  }
  const tab = useTabBarStore();
  watch(
    () => tab.getTabList,
    (tabList) => {
      console.log(tabList);
      if (tabList) {
        // window.electronAPI?.syncTab(JSON.stringify(tabList));
      }
    },
    { deep: true }
  );
};

const keyEmitTime: Recordable = {};

export const useElectronStoreSync = () => {
  if (!window.electronAPI) {
    return;
  }

  const initStoreStr = window.localStorage.getItem('electron-store');
  let lastState = initStoreStr || '{}';
  let syncTime = 0;
  let lock = false;
  let fullPath = '';
  const emitState = debounce((state: any, uuid: any, changeStage) => {
    if (!Object.keys(changeStage).length) {
      return;
    }
    const obj: Recordable = {};
    // if (changeStage && Object.keys(changeStage).length) {
    //   lastState = JSONStringify(changeStage);
    // }
    Object.keys(state).forEach((key: string) => {
      if (whiteStoreIds.includes(key)) {
        obj[key] = toRaw(state[key]);
        if (
          changeStage &&
          Object.keys(changeStage).length &&
          changeStage[key]
        ) {
          obj[key] = { ...obj[key], ...changeStage[key] };
        }
      }
    });
    lastState = JSONStringify(obj);
    window.localStorage.setItem('electron-store', lastState);
    window.electronAPI?.emitStore(
      JSONStringify(changeStage),
      fullPath,
      uuid || null
    );
  }, 0);

  const initStateFunc = (store: any, versionTimes: any) => {
    // 作为接收者，无需再发起同步
    lock = true;
    Object.keys(store).forEach((key: string) => {
      const keyStates = store[key];
      Object.keys(keyStates).forEach((k1: string) => {
        if (!pinia.state.value[key]) {
          pinia.state.value[key] = {};
        }
        if (versionTimes && versionTimes[`${key}-${k1}`]) {
          if (
            !keyEmitTime[`${key}-${k1}`] ||
            versionTimes[`${key}-${k1}`] > keyEmitTime[`${key}-${k1}`]
          ) {
            pinia.state.value[key][k1] = keyStates[k1];
            keyEmitTime[`${key}-${k1}`] = versionTimes[`${key}-${k1}`];
          }
        } else {
          pinia.state.value[key][k1] = keyStates[k1];
        }
      });
    });
    setTimeout(() => {
      lock = false;
    }, 20);
  };

  if (lastState !== '{}') {
    initStateFunc(JSONParse(lastState));
    // 通知主进程存哪个页面需要同步 mainPage 的store
    if (isElectronSinglePage()) {
      // setTimeout(() => {
      //   emitState(pinia.state.value, UUID);
      // }, 1000);
    }
  }

  window.electronAPI?.onUpdateStore(
    debounce((storeStr: string, path: string, needUUID: any) => {
      if (needUUID && needUUID !== UUID) {
        return;
      }
      fullPath = path;
      const lastStateObj = JSONParse(lastState);
      const store: Recordable = JSONParse(storeStr);
      const versionTimes = store.versionTime;
      delete store.versionTime;
      let dataHasChanged = false;
      Object.keys(store).forEach((key: string) => {
        const keyStates = store[key];
        Object.keys(keyStates).forEach((k1: string) => {
          if (!isEqual(keyStates[k1], lastStateObj[key]?.[k1])) {
            dataHasChanged = true;
          }
        });
      });
      if (!dataHasChanged) {
        return;
      }
      syncTime += 1;
      console.log('sync time', syncTime);
      initStateFunc(store, versionTimes);
    }, 0)
  );
  // 忽略监听 app 以及 router store
  const watchStates = {};
  whiteStoreIds
    .filter((item) => item !== 'app')
    .forEach((id: string) => {
      watchStates[id] = pinia.state.value[id];
    });
  let oldState;
  const initOldStage = (newState: any) => {
    oldState = {};
    Object.keys(newState).forEach((key: string) => {
      oldState[key] = cloneDeep(toRaw(newState[key]));
    });
  };
  watch(
    () => watchStates,
    (newState) => {
      if (!oldState) {
        initOldStage(newState);
      }
      if (lock) {
        return;
      }
      const newKeys = Object.keys(newState);
      const changeStage: Recordable = {};
      const now = new Date().getTime();
      if (oldState) {
        newKeys.forEach((key: string) => {
          const keyStates = toRaw(newState[key]);
          if (!keyStates) {
            return;
          }
          Object.keys(keyStates).forEach((k1: string) => {
            if (!isEqual(keyStates[k1], oldState[key]?.[k1])) {
              if (!changeStage[key]) {
                changeStage[key] = {};
              }
              changeStage[key][k1] = keyStates[k1];
              keyEmitTime[`${key}-${k1}`] = now;
            }
          });
        });
      }

      initOldStage(newState);

      // oldState = structuredClone(toRaw(newState));
      if (Object.keys(changeStage).length) {
        changeStage.versionTime = keyEmitTime;
      }
      emitState(pinia.state.value, null, changeStage);
    },
    { deep: true, immediate: true }
  );
};

let canSetupFlag = false;
export const useElectronDefaultConfig = (setDefaultConfig: any) => {
  window.electronAPI?.onDefaultConfigUpdate((config: string) => {
    console.log('config-updated', config);
    if (canSetupFlag) {
      canSetupFlag = false;
      return;
    }
    setDefaultConfig(JSONParse(config));
  });
  return (params: Recordable) => {
    canSetupFlag = true;
    window.electronAPI?.emitDefaultConfig(JSONStringify(params));
    setDefaultConfig(params);
  };
};

export const electronInitRoute = (result: RouteRecordNormalized[]) => {
  const electronResult: any[] = [];

  if (window.electronAPI?.electronPage || window.electronAPI?.singlePage) {
    const emptyRoute = () => import('@/components/qsIframe/index.vue');
    result.forEach((item: any) => {
      if (item.children && item.children.length) {
        item.children?.forEach((itx: any) => {
          const newRoute = { ...itx };
          itx.component = emptyRoute;

          newRoute.path = `${item.path}/${newRoute.path}-electron`;

          if (window.electronAPI?.singlePage) {
            itx.name = `${itx.name}-EMPTY`;
          } else {
            newRoute.name += 'Electron';
          }
          electronResult.push(newRoute);
        });
      } else {
        const newRoute = { ...item };
        item.component = emptyRoute;

        if (window.electronAPI?.singlePage) {
          item.name = `${item.name}-EMPTY`;
        } else {
          newRoute.name += 'Electron';
        }
        newRoute.path = `${newRoute.path}-electron`;
        // newRoute.name += 'Electron';
        electronResult.push(newRoute);
      }
    });
    window.electronAPI?.initRoute?.(JSON.stringify(electronResult));
  }
  return electronResult;
};

export const isElectronSinglePage = () => {
  return window.electronAPI?.singlePage;
};

export const isElectronMainPage = () => {
  return window.electronAPI?.electronPage;
};

export const Actions = {
  LOGOUT: 'LOGOUT',
  FACTORY_LOGIN: 'FACTORY_LOGIN',
  TAB_RELOAD: 'reload',
  TAB_CLOSE: 'current',
  TAB_CLOSE_LEFT: 'left',
  TAB_CLOSE_RIGHT: 'right',
  TAB_CLOSE_OTHER: 'others',
  TAB_CLOSE_ALL: 'all',
};

export const electronSwitchLogout = (action: string) => {
  window.electronAPI?.switchAction(action);
};

export const electronSwitchTab = (action: string, pos: Recordable) => {
  window.electronAPI?.switchAction(action, JSON.stringify(pos));
};

export const initUpdater = (needInstall: any) => {
  if (!window.electronAPI || !window.electronAPI?.electronPage) {
    return () => {};
  }
  window.electronAPI?.updateAvailable((data: any) => {
    console.log('updateAvailable', data);
    // 立即确认更新
    window.electronAPI?.confirmUpdate();
  });

  window.electronAPI?.downloadProgress((data: any) => {
    console.log('downloadProgress', data);
  });

  window.electronAPI?.updateDownloaded((data: any) => {
    console.log('updateDownloaded', data);
    needInstall.value = true;
  });
  setTimeout(() => {
    window.electronAPI?.checkForUpdates();
  }, 1000);
  return () => {
    // 立即更新
    window.electronAPI?.updateNow();
  };
};

class ElectronGlobalActions {
  actions: Recordable = {};

  tabActions: Recordable = {};

  /* eslint-disable new-cap */
  constructor() {
    this.actions = {};
    this.tabActions = {};
    if (window.electronAPI?.electronPage) {
      window.electronAPI?.onUpdateActions(
        (action: string, typeInfo: string) => {
          this.doAction(action, JSON.parse(typeInfo));
        }
      );
    }
  }

  collectActions(
    action: string,
    actionFunc: () => void,
    typeInfo?: Recordable
  ) {
    if (typeInfo && typeInfo.type === 'tab') {
      if (!this.tabActions[typeInfo.index]) {
        this.tabActions[typeInfo.index] = {};
      }
      this.tabActions[typeInfo.index][action] = actionFunc;
      return;
    }
    this.actions[action] = actionFunc;
  }

  collectTabActions(actionFunc: (val: any) => void, index: number) {
    const typeInfo = {
      type: 'tab',
      index,
    };
    this.collectActions(
      Actions.TAB_RELOAD,
      () => actionFunc(Actions.TAB_RELOAD),
      typeInfo
    );
    this.collectActions(
      Actions.TAB_CLOSE,
      () => actionFunc(Actions.TAB_CLOSE),
      typeInfo
    );
    this.collectActions(
      Actions.TAB_CLOSE_LEFT,
      () => actionFunc(Actions.TAB_CLOSE_LEFT),
      typeInfo
    );
    this.collectActions(
      Actions.TAB_CLOSE_RIGHT,
      () => actionFunc(Actions.TAB_CLOSE_RIGHT),
      typeInfo
    );
    this.collectActions(
      Actions.TAB_CLOSE_OTHER,
      () => actionFunc(Actions.TAB_CLOSE_OTHER),
      typeInfo
    );
    this.collectActions(
      Actions.TAB_CLOSE_ALL,
      () => actionFunc(Actions.TAB_CLOSE_ALL),
      typeInfo
    );
  }

  doAction(action: string, typeInfo: Recordable) {
    const tabActionList = [
      Actions.TAB_RELOAD,
      Actions.TAB_CLOSE,
      Actions.TAB_CLOSE_LEFT,
      Actions.TAB_CLOSE_RIGHT,
      Actions.TAB_CLOSE_OTHER,
      Actions.TAB_CLOSE_ALL,
    ];
    if (tabActionList.includes(action)) {
      this.tabActions[typeInfo.tabActionIndex][action]?.();
      return;
    }
    this.actions[action]?.();
  }
}

// 这个方法是为了解决electron无法执行vue页面文件中onActivated的方法
export const electronMixin = (app: Recordable) => {
  if (isElectronSinglePage()) {
    app.mixin({
      created() {
        if (this.$route) {
          try {
            const instance = getCurrentInstance();
            const componentName = instance?.type?.name;

            if (componentName) {
              // 检查当前路由匹配的组件名称
              const matchedRouteComponents = this.$route.matched
                .map((record: Recordable) => record.components?.default)
                .filter(Boolean);

              const isCurrentRouteComponent = matchedRouteComponents.some(
                (comp: Recordable) => comp?.name === componentName
              );

              if (isCurrentRouteComponent) {
                useElectronOnActivated();
              }
            }
          } catch (error) {
            console.error(error);
          }
        }
      },
    });
  }
};

export const electronActions = new ElectronGlobalActions();
